import { FC, useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { KTSVG } from '../../../../_metronic/helpers'
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AddCrewModal from './AddCrewModal'
import { getCrewList, getRanks, updateCrewStatus, getRanksforList, updateCrewApprovalStatus } from '../core/_requests'
import { Crew, Rank } from '../core/_models'
import ViewCrewDetailsModal from './ViewCrewDetailsModal'
import EditCrewModal from './EditCrewModal'
import DeleteCrewModal from './DeleteCrewModal';
import { TrailRecordsModal } from './TrailRecordsModal'
import { useAuth } from '../../auth';
import { CREWING_ACTION_PERMISSIONS, CREWING_READ_PERMISSIONS, useCrewingPermissionFetch } from '../core/crewingPermissionAccess'
import { CrewUploadModal } from './CrewUploadModal'
import VesselLandingLink from '../../../shared/components/VesselLandingLink'
import html2canvas from 'html2canvas'
import { jsPDF } from 'jspdf'
import html2pdf from 'html2pdf.js';
import SearchableSelect from '../../../shared/components/SearchableSelect'
import { TableSkeleton } from '../../../shared/components/LoadingSkeletons'
import { fetchAccessibleVesselsForCurrentUser, normalizeAccessibleVessels } from '../../../shared/access/accessibleVessels'
import { useGlobalFilter } from '../../../shared/context/GlobalFilterContext'
import { useCrewingRoleAccess } from '../core/crewingRoleAccess'

const CrewList: FC = () => {
    const [isAddCrewModalOpen, setIsAddCrewModalOpen] = useState<boolean>(false);
    const [isUploadModalOpen, setIsUploadModalOpen] = useState<boolean>(false);
    const [isEditCrewModalOpen, setIsEditCrewModalOpen] = useState<boolean>(false);
    const [isDeleteCrewModalOpen, setIsDeleteCrewModalOpen] = useState<boolean>(false);
    const [isViewCrewDetailsModalOpen, setIsViewCrewDetailsModalOpen] = useState<boolean>(false);
    const [crew, setCrew] = useState<Crew[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [selectedCrew, setSelectedCrew] = useState<Crew>();
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [vesselList, setVesselList] = useState<any[]>([]);  // Add state for vessels
    const [selectedVessel, setSelectedVessel] = useState<string>('all');  // To filter by selected vessel
    const [selectedActiveStatus, setSelectedActiveStatus] = useState<'all' | 'active' | 'inactive' | 'pending'>('all'); // Active/Inactive Filter
    // const filteredSubCompanies = crew.filter(crew =>
    //     crew.name?.toLowerCase().includes(searchTerm.toLowerCase())

    // );
    // const currentCrew = filteredSubCompanies.slice(indexOfFirstCompany, indexOfLastCompany);
    // const totalPages = Math.ceil(filteredSubCompanies.length / rowsPerPage);
    const [showCrewCreds, setShowCrewCreds] = useState(false);
    const [newCrewPassword, setNewCrewPassword] = useState<string | null>(null);
    const [crewLoginLink, setCrewLoginLink] = useState<string | null>(null);

    const [ranks, setRanks] = useState<Rank[]>([]);
    const [rankMap, setRankMap] = useState<Record<number, string>>({});

    // Sorting State
    const [sortColumn, setSortColumn] = useState<string>(''); // Default sort by name
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc'); // Default is ascending
    const [addActive, setAddActive] = useState(false)


    const { currentUser, hasPermission } = useAuth()
    const {
        isSuperAdmin,
        isCompanyGroupAdmin,
        isCompanyAdmin,
        isOperator: baseIsOperator,
        isOfficeRole,
    } = useCrewingRoleAccess()
    const { state: globalState, vessels: globalVessels } = useGlobalFilter()

    // Sync global header search query → local searchTerm
    useEffect(() => {
        setSearchTerm(globalState.searchQuery || '')
    }, [globalState.searchQuery])
    const roleEntityId = currentUser?.roleEntityId;
    const roleId = currentUser?.role?.id;
    const cgaId = currentUser?.companyGroupAdminId ?? (currentUser as any)?.companyGroupAdmin?.id ?? null;
    const isOperator = baseIsOperator || roleId === 6;
    const operatorCompanyAdminId = currentUser?.companyAdminId ?? (currentUser as any)?.companyAdmin?.id ?? null;
    const operatorCompanyGroupAdminId = currentUser?.companyGroupAdminId ?? (currentUser as any)?.companyGroupAdmin?.id ?? null;
    const operatorActsLikeCompanyAdmin = isOperator && operatorCompanyAdminId != null;
    const operatorActsLikeGroupAdmin = isOperator && !operatorActsLikeCompanyAdmin && operatorCompanyGroupAdminId != null;

    const [trailCrewId, setTrailCrewId] = useState<number | null>(null)
    const [trailCrewName, setTrailCrewName] = useState<string>('');
    const [showIMOExportModal, setShowIMOExportModal] = useState<boolean>(false);
    const [showVesselFilterAlert, setShowVesselFilterAlert] = useState<boolean>(false);
    const resolveCrewVesselId = (item: Crew) => item.vessel?.id ?? item.vesselId ?? null

    const fetchVessels = async () => {
        try {
            const vesselList = normalizeAccessibleVessels(await fetchAccessibleVesselsForCurrentUser());

            const vesselsForCompany = vesselList.filter(vessel => {
                const isActive = vessel.active;

                if (roleId === 1 || isSuperAdmin) {
                    return isActive;
                } else if (roleId === 5 || isCompanyGroupAdmin) {
                    return isActive && vessel.companyGroupAdmin?.id === (roleEntityId ?? cgaId);
                } else if (roleId === 2 || isCompanyAdmin || operatorActsLikeCompanyAdmin) {
                    const companyId = (roleId === 2 || isCompanyAdmin) ? (roleEntityId ?? currentUser?.companyAdminId) : operatorCompanyAdminId;
                    return isActive && vessel.companyAdmin?.id === companyId;
                } else if (operatorActsLikeGroupAdmin) {
                    return isActive && vessel.companyGroupAdmin?.id === operatorCompanyGroupAdminId;
                } else if (isOperator || isOfficeRole) {
                    return isActive;
                }

                return false;
            });

            setVesselList(vesselsForCompany);
        } catch (error) {
            console.error('Failed to fetch vessel list:', error);
        }
    };

    const canBulkUpload =
        isSuperAdmin || hasPermission(CREWING_ACTION_PERMISSIONS.BULK_UPLOAD_CREW_MASTER)
    const canDeactivateCrew =
        isSuperAdmin || hasPermission(CREWING_ACTION_PERMISSIONS.DEACTIVATE_CREW_MASTER)

    const fetchCrew = useCallback(async () => getCrewList(), [])

    useCrewingPermissionFetch({
        permissionCode: CREWING_READ_PERMISSIONS.CREW_MASTER,
        fetcher: fetchCrew,
        onSuccess: (crewList) => {
            setCrew(crewList)
            setLoading(false)
        },
        onDenied: () => {
            setCrew([])
            setLoading(false)
        },
        onError: (error) => {
            console.error('Failed to fetch crew list:', error)
            setCrew([])
            setLoading(false)
        },
    })

    useEffect(() => {
        fetchVessels();
    }, [roleId, roleEntityId, operatorActsLikeCompanyAdmin, operatorActsLikeGroupAdmin, operatorCompanyAdminId, operatorCompanyGroupAdminId]);

    useEffect(() => {
        if (crew.length > 0 && searchTerm) {
            const hasMatch = crew.some(c => c.name?.toLowerCase().includes(searchTerm.toLowerCase()));
            if (!hasMatch) {
                setSearchTerm(''); // Clear search if it causes empty view
            }
        }
    }, [crew]);

    // Sorting logic
    const handleSort = (column: string) => {
        if (sortColumn === column) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumn(column);
            setSortOrder('asc'); // Default to ascending for a new column
        }
    };

    const sortedAndFilteredCrew = [...crew]
        .filter((crew) => {
            let matchesVessel = true
            const isPending = crew.approvalStatus === 'PENDING';
            if (globalState.vessel?.id) {
                if (isPending) {
                    const vesselCga = Number((globalState.vessel as any).company_group_admin_id ?? (globalState.vessel as any).cgaId ?? (globalState.vessel as any).companyGroupAdmin?.id ?? (globalState.vessel as any).companyGroupAdminId ?? 0);
                    const vesselCa = Number((globalState.vessel as any).company_admin_id ?? (globalState.vessel as any).caId ?? (globalState.vessel as any).companyAdmin?.id ?? (globalState.vessel as any).companyAdminId ?? 0);
                    const crewCga = Number(crew.companyGroupAdminId ?? (crew as any).companyGroupAdmin?.id ?? 0);
                    const crewCa = Number(crew.companyAdminId ?? (crew as any).companyAdmin?.id ?? 0);
                    if (vesselCga || vesselCa) {
                        matchesVessel = (vesselCga > 0 && crewCga === vesselCga) || (vesselCa > 0 && crewCa === vesselCa) || (!crewCga && !crewCa);
                    } else {
                        matchesVessel = true;
                    }
                } else {
                    const crewVesselId = crew.vessel?.id ?? crew.vesselId
                    matchesVessel = crewVesselId != null ? Number(crewVesselId) === Number(globalState.vessel.id) : false
                }
            } else if (globalState.company) {
                const companyId = Number(globalState.company.id);
                const isCompanyGroup = globalState.company.type === 'company_group';
                const allowedVesselIds = new Set(globalVessels.map((v: any) => Number(v.id)))
                const crewCga = Number(crew.companyGroupAdminId ?? (crew as any).companyGroupAdmin?.id ?? 0);
                const crewCa = Number(crew.companyAdminId ?? (crew as any).companyAdmin?.id ?? 0);
                const directCompanyMatch = isCompanyGroup
                    ? (crewCga === companyId || (!crewCga && !crewCa))
                    : (crewCa === companyId || crewCga === companyId || (!crewCga && !crewCa));
                const crewVesselId = crew.vessel?.id ?? crew.vesselId
                const vesselCompanyMatch = crewVesselId != null ? allowedVesselIds.has(Number(crewVesselId)) : false;
                matchesVessel = directCompanyMatch || vesselCompanyMatch;
            }

            const matchesActiveStatus =
                selectedActiveStatus === 'all' ||
                (selectedActiveStatus === 'active' && crew.active && crew.approvalStatus === 'APPROVED') ||
                (selectedActiveStatus === 'inactive' && !crew.active) ||
                (selectedActiveStatus === 'pending' && crew.approvalStatus === 'PENDING');
            return crew.name?.toLowerCase().includes(searchTerm.toLowerCase()) && matchesVessel && matchesActiveStatus;

        })
        .sort((a, b) => {
            // 🔀 newest first by id if no explicit sortColumn
            if (!sortColumn) {
                return b.id - a.id;
            }

            const aValue = a[sortColumn as keyof Crew] || '';
            const bValue = b[sortColumn as keyof Crew] || '';

            // Handle nested props and rank…
            if (sortColumn === 'vessel') {
                const va = a.vessel?.fleet_name || '';
                const vb = b.vessel?.fleet_name || '';
                return sortOrder === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
            }
            if (sortColumn === 'rank') {
                return sortOrder === 'asc'
                    ? a.rankId - b.rankId
                    : b.rankId - a.rankId;
            }

            // fallback string compare
            if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
            return 0;
        });

    // Filter crew by vessel
    // const filteredCrew = crew.filter((crew) => {
    //     const matchesVessel =
    //         selectedVessel === 'all' ||
    //         crew.vessel?.fleet_name === selectedVessel ||
    //         (selectedVessel === 'onLeave' && !crew.vessel);
    //     return crew.name?.toLowerCase().includes(searchTerm.toLowerCase()) && matchesVessel;
    // });

    // Paginate filtered crew
    const indexOfLastCrew = currentPage * rowsPerPage;
    const indexOfFirstCrew = indexOfLastCrew - rowsPerPage;
    const currentCrew = sortedAndFilteredCrew.slice(indexOfFirstCrew, indexOfLastCrew);
    const totalPages = Math.ceil(sortedAndFilteredCrew.length / rowsPerPage);

    useEffect(() => {
        console.log(sortedAndFilteredCrew);
        if (currentPage > totalPages) {
            setCurrentPage(1);
        }
    }, [sortedAndFilteredCrew, rowsPerPage]);

    // ranks once on mount
    // ranks once on mount
    useEffect(() => {
        getRanksforList()
            .then(rs => {
                setRanks(rs);
                const m: Record<number, string> = {};
                rs.forEach(r => m[r.id] = r.rank);
                setRankMap(m);
            })
            .catch(err => console.error('Failed to load ranks', err));
    }, []);

    const handleToggleActiveStatus = (crewId: number, isActive: boolean) => {
        const crewToUpdate = crew.find((c) => c.id === crewId); // Find the specific crew

        if (!crewToUpdate) {
            console.error('Crew not found');
            return;
        }

        if (isActive) {
            setSelectedCrew(crewToUpdate); // Set the selected crew
            setIsDeleteCrewModalOpen(true); // Show confirmation modal for deactivating
        } else {
            // Directly update status to active if inactive
            updateCrewStatus(crewId, true).then(() => {
                fetchCrew();
                toast.success('Crew marked as active');
            }).catch((err) => {
                console.error('Failed to update crew status:', err);
                toast.error('Failed to update crew status');
            });
        }
    };

    const handleUpdateApprovalStatus = async (crewId: number, status: 'APPROVED' | 'REJECTED') => {
        try {
            await updateCrewApprovalStatus(crewId, status);
            toast.success(`Crew status updated to ${status.toLowerCase()} successfully`);
            const crewList = await getCrewList();
            setCrew(crewList);
        } catch (error: any) {
            console.error('Failed to update crew approval status:', error);
            toast.error('Failed to update crew approval status');
        }
    };

    // detect if any filter is non-default
    const isAnyFilterActive =
        searchTerm.trim() !== '' ||
        selectedActiveStatus !== 'all';

    // clear them all
    const clearFilters = () => {
        setSearchTerm('');
        setSelectedActiveStatus('all');
        setCurrentPage(1);
    };


    return (
        <div
            className='app-main flex-column flex-row-fluid'
            id='kt_app_main'
            style={{ height: '100vh' }}
        >
            <div className='d-flex flex-column flex-column-fluid' >
                <div
                    id='kt_app_content'
                    className='app-content flex-column-fluid d-flex flex-column '
                    style={{ flex: 1 }}
                >
                    <div className='card flex-column-fluid d-flex flex-column border-top' style={{ flex: 1, background: '#ffffff' }}>
                        {/* Header */}
                        <div className='d-flex gap-3 pe-5 ps-5'>
                            <div className='py-3 ms-3' style={{ flex: 5 }}>
                                <h3 className='card-title fw-bold text-dark mb-5'>Crew Members</h3>
                                <div className='d-flex justify-content-between mb-5'>
                                    <div className='d-flex align-items-center gap-2'>
                                        <input
                                            type="text"
                                            className="form-control cp_search_input"
                                            placeholder="Crew Name"
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            style={{ width: '220px' }}
                                        />
                                        <div style={{ width: '180px', minWidth: '180px' }}>
                                            <SearchableSelect
                                                className="form-select"
                                                value={selectedActiveStatus}
                                                onChange={(e) => setSelectedActiveStatus(e.target.value as 'all' | 'active' | 'inactive' | 'pending')}
                                            >
                                                <option value="all">All Status</option>
                                                <option value="active">Active</option>
                                                <option value="inactive">Inactive</option>
                                                <option value="pending">Pending Approval</option>
                                            </SearchableSelect>
                                        </div>
                                        {/* ← Clear Filters */}
                                        <button
                                            className="btn btn-outline-secondary"
                                            disabled={!isAnyFilterActive}
                                            onClick={clearFilters}
                                        >
                                            Clear&nbsp;Filters
                                        </button>
                                        <div className='d-flex align-items-center gap-2'>
                                            <button
                                                className='btn text-white fw-bold shadow-sm d-inline-flex align-items-center gap-2'
                                                style={{
                                                    backgroundColor: 'rgb(29, 78, 216)',
                                                    borderColor: 'rgb(29, 78, 216)',
                                                    borderRadius: '9.75px',
                                                    padding: '8px 13px',
                                                    fontSize: '11.5px',
                                                    fontWeight: 700,
                                                    boxShadow: 'rgba(0, 0, 0, 0.08) 0px 2px 8px',
                                                    transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
                                                    gap: '6.5px',
                                                }}
                                                onClick={() => setIsAddCrewModalOpen(true)}
                                            >
                                                <svg
                                                    xmlns='http://www.w3.org/2000/svg'
                                                    width='14'
                                                    height='14'
                                                    viewBox='0 0 24 24'
                                                    fill='none'
                                                    stroke='currentColor'
                                                    strokeWidth='2'
                                                    strokeLinecap='round'
                                                    strokeLinejoin='round'
                                                    style={{ width: '13px', height: '13px' }}
                                                >
                                                    <line x1='12' y1='5' x2='12' y2='19'></line>
                                                    <line x1='5' y1='12' x2='19' y2='12'></line>
                                                </svg>
                                                Add Crew
                                            </button>
                                            {(isSuperAdmin || hasPermission('BULK_UPLOAD_CREW_MASTER')) && (
                                                <button
                                                    className='btn text-white fw-bold shadow-sm d-inline-flex align-items-center gap-2'
                                                    style={{
                                                        backgroundColor: 'rgb(16, 185, 129)',
                                                        borderColor: 'rgb(16, 185, 129)',
                                                        borderRadius: '9.75px',
                                                        padding: '8px 13px',
                                                        fontSize: '11.5px',
                                                        fontWeight: 700,
                                                        boxShadow: 'rgba(0, 0, 0, 0.08) 0px 2px 8px',
                                                        transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
                                                        gap: '6.5px',
                                                    }}
                                                    onClick={() => setIsUploadModalOpen(true)}
                                                >
                                                    <svg
                                                        xmlns='http://www.w3.org/2000/svg'
                                                        width='14'
                                                        height='14'
                                                        viewBox='0 0 24 24'
                                                        fill='none'
                                                        stroke='currentColor'
                                                        strokeWidth='2'
                                                        strokeLinecap='round'
                                                        strokeLinejoin='round'
                                                        style={{ width: '13px', height: '13px' }}
                                                    >
                                                        <path d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4'></path>
                                                        <polyline points='17 8 12 3 7 8'></polyline>
                                                        <line x1='12' y1='3' x2='12' y2='15'></line>
                                                    </svg>
                                                    Bulk Upload
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                {/* Summary Cards */}
                                <div className='py-3'>
                                    {/* Reports Table */}
                                    <div className="report-table table-responsive" style={{ maxHeight: '50rem', overflowY: 'auto' }}>
                                        <table className="table table-bordered align-middle">
                                            <thead className="table-header py-5">
                                                <tr>
                                                    <th>
                                                        CREW NAME
                                                        <button
                                                            onClick={() => handleSort('name')}
                                                            className="btn btn-link p-0 m-0 pb-1"
                                                            disabled={crew.length === 0}
                                                        >
                                                            <KTSVG path={`/media/map/sort-col-${sortColumn === 'name' ? (sortOrder === 'asc' ? 'up-black' : 'down-black') : 'grey'}.svg`} />
                                                        </button>
                                                    </th>
                                                    <th>
                                                        VESSEL
                                                        <button
                                                            onClick={() => handleSort('vessel')}
                                                            className="btn btn-link p-0 m-0 pb-1"
                                                            disabled={crew.length === 0}
                                                        >
                                                            <KTSVG path={`/media/map/sort-col-${sortColumn === 'vessel' ? (sortOrder === 'asc' ? 'up-black' : 'down-black') : 'grey'}.svg`} />
                                                        </button>
                                                    </th>
                                                    <th>
                                                        RANK
                                                        <button
                                                            onClick={() => handleSort('rank')}
                                                            className="btn btn-link p-0 m-0 pb-1"
                                                            disabled={crew.length === 0}
                                                        >
                                                            <KTSVG path={`/media/map/sort-col-${sortColumn === 'rank' ? (sortOrder === 'asc' ? 'up-black' : 'down-black') : 'grey'}.svg`} />
                                                        </button>
                                                    </th>
                                                    <th>
                                                        NATIONALITY
                                                        <button
                                                            onClick={() => handleSort('nationality')}
                                                            className="btn btn-link p-0 m-0 pb-1"
                                                            disabled={crew.length === 0}
                                                        >
                                                            <KTSVG path={`/media/map/sort-col-${sortColumn === 'nationality' ? (sortOrder === 'asc' ? 'up-black' : 'down-black') : 'grey'}.svg`} />
                                                        </button>
                                                    </th>
                                                    <th>ACTIONS</th>
                                                </tr>
                                            </thead>
                                            <tbody className="table-body">
                                                {loading ? (
                                                    <TableSkeleton rows={rowsPerPage || 10} cols={5} />
                                                ) : currentCrew.length === 0 ? (
                                                    <tr>
                                                        <td colSpan={5} className="text-center py-4 text-muted">
                                                            No crew found
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    currentCrew
                                                        .map((crew, idx) => (
                                                            <tr key={idx}>
                                                                <td
                                                                    className="clickable"
                                                                    onClick={() => {
                                                                        setTrailCrewId(crew.id);
                                                                        setTrailCrewName(crew.name);
                                                                    }}
                                                                    style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                                                >
                                                                    {crew.name}
                                                                    {crew.approvalStatus === 'PENDING' && (
                                                                        <span className="badge badge-light-warning ms-2 fw-bold">Pending Approval</span>
                                                                    )}
                                                                    {crew.approvalStatus === 'REJECTED' && (
                                                                        <span className="badge badge-light-danger ms-2 fw-bold">Rejected</span>
                                                                    )}
                                                                </td>
                                                                <td>
                                                                    {crew.vessel?.fleet_name ? (
                                                                        <VesselLandingLink
                                                                            vesselId={resolveCrewVesselId(crew)}
                                                                            vesselName={crew.vessel.fleet_name}
                                                                            className='text-dark fw-bold text-hover-primary'
                                                                        />
                                                                    ) : (
                                                                        <span className='badge badge-light-warning'>ON LEAVE</span>
                                                                    )}
                                                                </td>
                                                                <td>{rankMap[crew.rankId] ?? '—'}</td>
                                                                <td>{crew.nationality}</td>
                                                                <td>
                                                                    <button
                                                                        className="btn btn-sm px-0"
                                                                        onClick={() => {
                                                                            setIsEditCrewModalOpen(true);
                                                                            setSelectedCrew(crew);
                                                                        }}
                                                                    >
                                                                        <KTSVG path="/media/map/edit-active.svg" className="" />
                                                                    </button>
                                                                    <button
                                                                        className="btn btn-sm px-0"
                                                                        onClick={() => {
                                                                            setIsViewCrewDetailsModalOpen(true);
                                                                            setSelectedCrew(crew);
                                                                        }}
                                                                    >
                                                                        <KTSVG path="/media/map/ph_eye.svg" className="svg-icon-2" />
                                                                    </button>
                                                                    {!crew.vessel?.fleet_name && (isSuperAdmin || hasPermission('DEACTIVATE_CREW_MASTER')) && (
                                                                        <button
                                                                            className="btn btn-sm px-0"
                                                                            onClick={() => handleToggleActiveStatus(crew.id, crew.active)}
                                                                        >
                                                                            <KTSVG
                                                                                path={
                                                                                    crew.active
                                                                                        ? '/media/map/toggle-on-green.svg'
                                                                                        : '/media/map/toggle-off-grey.svg'
                                                                                }
                                                                                className="svg-icon-2"
                                                                            />
                                                                        </button>
                                                                    )}
                                                                    {(isSuperAdmin || hasPermission('APPROVE_REJECT_CREW_MASTER')) && crew.approvalStatus === 'PENDING' && (
                                                                        <>
                                                                            <button
                                                                                className="btn btn-sm btn-icon btn-light-success btn-active-success ms-1 me-1"
                                                                                style={{ width: '25px', height: '25px' }}
                                                                                title="Approve"
                                                                                onClick={() => handleUpdateApprovalStatus(crew.id, 'APPROVED')}
                                                                            >
                                                                                <i className="bi bi-check fs-4"></i>
                                                                            </button>
                                                                            <button
                                                                                className="btn btn-sm btn-icon btn-light-danger btn-active-danger"
                                                                                style={{ width: '25px', height: '25px' }}
                                                                                title="Reject"
                                                                                onClick={() => handleUpdateApprovalStatus(crew.id, 'REJECTED')}
                                                                            >
                                                                                <i className="bi bi-x fs-4"></i>
                                                                            </button>
                                                                        </>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ))
                                                )}
                                            </tbody>
                                        </table>
                                        {/* Pagination */}
                                        <div className="pagination-wrapper d-flex justify-content-between align-items-center">
                                            <div>
                                                Rows per page
                                                <SearchableSelect
                                                    className="form-select d-inline-block w-auto ms-2"
                                                    style={{ borderRadius: "20px" }}
                                                    value={rowsPerPage}
                                                    onChange={(e) => {
                                                        setRowsPerPage(parseInt(e.target.value));
                                                        setCurrentPage(1); // Reset to page 1
                                                    }}
                                                >
                                                    <option value={10}>10</option>
                                                    <option value={20}>20</option>
                                                    <option value={50}>50</option>
                                                </SearchableSelect>

                                            </div>

                                            <nav>
                                                <ul className="pagination">
                                                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                        <button className="page-link" onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}>‹</button>
                                                    </li>
                                                    {Array.from({ length: totalPages }, (_, i) => (
                                                        <li key={i} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                                                            <button className="page-link" onClick={() => setCurrentPage(i + 1)}>{i + 1}</button>
                                                        </li>
                                                    ))}
                                                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                        <button className="page-link" onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}>›</button>
                                                    </li>
                                                </ul>
                                            </nav>

                                        </div>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>
                </div>
                {isAddCrewModalOpen && (
                    <AddCrewModal
                        isOpen={isAddCrewModalOpen}
                        onClose={() => { setIsAddCrewModalOpen(false) }}
                        onCrewAdded={(pwd, link) => {
                            setCrewLoginLink(link);
                            setNewCrewPassword(pwd);
                            setShowCrewCreds(true);
                            fetchCrew();             // refresh your list
                        }} />
                )}
                {isUploadModalOpen && (
                    <CrewUploadModal
                        visible={isUploadModalOpen}
                        onClose={() => setIsUploadModalOpen(false)}
                        onSuccess={() => {
                            fetchCrew();
                        }}
                    />
                )}
                {isDeleteCrewModalOpen && (
                    <DeleteCrewModal
                        isOpen={isDeleteCrewModalOpen}
                        onClose={() => { setIsDeleteCrewModalOpen(false) }}
                        onCrewDeleted={fetchCrew}
                        crewData={selectedCrew}
                    />
                )}
                {isEditCrewModalOpen && (
                    <EditCrewModal
                        isOpen={isEditCrewModalOpen}
                        onClose={() => { setIsEditCrewModalOpen(false) }}
                        onCrewUpdated={fetchCrew}
                        crewData={selectedCrew}
                    />
                )}
                {isViewCrewDetailsModalOpen && (
                    <ViewCrewDetailsModal
                        isOpen={isViewCrewDetailsModalOpen}
                        onClose={() => { setIsViewCrewDetailsModalOpen(false) }}
                        crewData={selectedCrew}
                    />
                )}
                {trailCrewId !== null && (
                    <TrailRecordsModal
                        crewId={trailCrewId}
                        crewName={trailCrewName}
                        onClose={() => setTrailCrewId(null)}
                    />
                )}

                {showCrewCreds && newCrewPassword && crewLoginLink && (
                    <div
                        className="modal fade show d-block"
                        tabIndex={-1}
                        style={{ background: 'rgba(0,0,0,0.5)' }}
                        onClick={() => setShowCrewCreds(false)}
                    >
                        <div
                            className="modal-dialog modal-dialog-centered"
                            onClick={e => e.stopPropagation()}
                        >
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Crew Created!</h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={() => setShowCrewCreds(false)}
                                    />
                                </div>
                                <div className="modal-body">
                                    <p><strong>Generated Password:</strong> {newCrewPassword}</p>
                                    <p className="text-danger">Please copy this password now. It will not be shown again.</p>

                                    <button
                                        className="btn btn-light mb-3"
                                        onClick={() => {
                                            navigator.clipboard.writeText(newCrewPassword);
                                            toast.success("Password copied to clipboard");
                                        }}
                                    >
                                        Copy Password to Clipboard
                                    </button>

                                    {/* <hr />

                                    <p><strong>Login Link:</strong></p>
                                    <a
                                        href={crewLoginLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-primary d-block mb-2"
                                    >
                                        {crewLoginLink}
                                    </a>

                                    <button
                                        className="btn btn-light"
                                        onClick={() => {
                                            navigator.clipboard.writeText(crewLoginLink);
                                            toast.success("Login link copied to clipboard");
                                        }}
                                    >
                                        Copy Login Link to Clipboard
                                    </button> */}
                                </div>

                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowCrewCreds(false)}>
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <ToastContainer
                    position="top-center"
                    autoClose={3000}
                    hideProgressBar={false}
                    newestOnTop={false}
                    closeOnClick
                    rtl={false}
                    pauseOnFocusLoss
                    draggable
                    pauseOnHover
                    style={{ top: "5rem" }}
                />
            </div>
        </div>

    )
}

export { CrewList }
